import { useCallback } from 'react'
import { useAdpSessionStore } from './useAdpSessionStore'
import { useAppDispatch } from '@/app/reduxHooks'
import { markValidationDirty } from '@/features/adp/state/validationSlice'

type SourceRole = 'parent' | 'child'

export interface SourceDataPayload {
  [key: string]: unknown
}

interface SourceDataEntry {
  role: SourceRole
  data: SourceDataPayload
  createdAt: number
}

interface NodeSourceWrapper {
  sourceData?: SourceDataEntry
}

export function useSourceData() {
  const { getNode, setNode, getAll } = useAdpSessionStore()
  const dispatch = useAppDispatch()

  /**
   * Set source data
   * First inserted source becomes parent automatically
   */
  const setSourceData = useCallback(
    (nodeId: string, payload: SourceDataPayload) => {
      const all = getAll()

      // Check if any parent already exists
      const hasParent = Object.values(all).some(node => {
        const n = node as NodeSourceWrapper
        return n?.sourceData?.role === 'parent'
      })

      const entry: SourceDataEntry = {
        role: hasParent ? 'child' : 'parent',
        data: payload,
        createdAt: Date.now(),
      }

      setNode(nodeId, {
        sourceData: entry,
      })
      dispatch(markValidationDirty())
    },
    [dispatch, getAll, setNode]
  )

  /**
   * Update existing source data (keeps role intact). If no entry exists
   * yet, falls back to creating one as a child to avoid silently dropping
   * the write — the silent no-op caused stale validator results.
   * Role defaults to 'child' so we never accidentally promote a node to
   * parent when a real parent already exists elsewhere.
   */
  const updateSourceData = useCallback(
    (nodeId: string, payload: SourceDataPayload) => {
      const existing = getNode(nodeId) as NodeSourceWrapper | null

      if (existing?.sourceData) {
        setNode(nodeId, {
          sourceData: {
            ...existing.sourceData,
            data: {
              ...existing.sourceData.data,
              ...payload,
            },
          },
        })
      } else {
        setNode(nodeId, {
          sourceData: {
            role: 'child',
            data: { ...payload },
            createdAt: Date.now(),
          },
        })
      }
      dispatch(markValidationDirty())
    },
    [dispatch, getNode, setNode]
  )

  /**
   * Get all source data (flat)
   */
  const getAllSourceData = useCallback((): SourceDataEntry[] => {
    const all = getAll()

    return Object.values(all)
      .map(node => (node as NodeSourceWrapper).sourceData)
      .filter((v): v is SourceDataEntry => Boolean(v))
  }, [getAll])

  /**
   * Get parent source (first inserted)
   */
  const getParentSource = useCallback((): SourceDataEntry | null => {
    const all = getAll()

    for (const node of Object.values(all)) {
      const entry = (node as NodeSourceWrapper).sourceData
      if (entry?.role === 'parent') return entry
    }

    return null
  }, [getAll])

  /**
   * Get all child sources
   */
  const getChildSources = useCallback((): SourceDataEntry[] => {
    const all = getAll()

    return Object.values(all)
      .map(node => (node as NodeSourceWrapper).sourceData)
      .filter((entry): entry is SourceDataEntry => entry?.role === 'child')
  }, [getAll])

  const getSourceDataByNodeId = useCallback(
    (nodeId: string): SourceDataEntry | null => {
      const node = getNode(nodeId) as NodeSourceWrapper | null
      return node?.sourceData ?? null
    },
    [getNode]
  )

  return {
    setSourceData,
    updateSourceData,
    getAllSourceData,
    getParentSource,
    getChildSources,
    getSourceDataByNodeId,
  }
}
